import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

/* ─── Animated word swap ─────────────────────────────── */
const words = ['Radiance', 'Elegance', 'Stories', 'Heritage'];
const WordSwap = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(p => (p + 1) % words.length), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={words[idx]}
        className="word-swap"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        {words[idx]}
      </motion.span>
    </AnimatePresence>
  );
};

/* ─── Hero images list ───────────────────────────────── */
const heroImages = [ '/hero1.png', '/hero.png', '/hero2.png'];

/* ─── Hero Section ───────────────────────────────────── */
const HeroSection = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);

  const [imgIdx, setImgIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setImgIdx(p => (p + 1) % heroImages.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root {
          --gold:      #B08D57;
          --gold-dark: #8A6D3E;
          --ink:       #1C1A18;
          --ink-soft:  #3D3A36;
          --cream:     #F4F2EE;
          --white:     #FFFFFF;
          --font-display: 'Playfair Display', Georgia, serif;
          --font-body:    'Cormorant Garamond', serif;
          --font-ui:      'Jost', sans-serif;
        }

        /* ── Hero wrapper ── */
        .hero-section {
          position: relative;
          width: 100%;
          min-height: calc(100vh - 162px);
          margin-top: 162px;
          background: var(--cream);
          display: flex;
          align-items: center;
          overflow: hidden;
        }

        /* ── Background image fills right 55% ── */
        .hero-bg {
          position: absolute;
          top: 0; right: 0;
          width: 65%;
          height: 100%;
          z-index: 0;
        }
        .hero-bg img {
          position: absolute;
          top: 0; left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center center;
          display: block;
        }
        /* fade from cream on left edge */
        .hero-bg::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to right, var(--cream) 0%, rgba(244,242,238,0.5) 40%, transparent 100%);
          z-index: 1;
        }

        /* ── Text content on left ── */
        .hero-content {
          position: relative;
          z-index: 2;
          width: 55%;
          padding: 60px clamp(32px, 5vw, 80px) 60px clamp(40px, 6vw, 100px);
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        /* ── Heading ── */
        .hero-h1 {
          font-family: var(--font-display);
          font-size: clamp(52px, 7vw, 96px);
          font-weight: 700;
          line-height: 1.0;
          color: var(--ink);
          letter-spacing: -0.02em;
          margin-bottom: 24px;
        }
        .hero-h1 .word-swap {
          display: block;
          color: var(--gold);
          font-style: italic;
          font-weight: 400;
        }

        /* ── Sub ── */
        .hero-sub {
          font-family: var(--font-body);
          font-size: clamp(15px, 1.8vw, 19px);
          font-weight: 300;
          color: var(--ink-soft);
          line-height: 1.7;
          max-width: 480px;
          margin-bottom: 36px;
          letter-spacing: 0.01em;
        }

        /* ── CTAs ── */
        .hero-ctas {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          align-items: center;
        }
        .btn-gold {
          font-family: var(--font-ui);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          background: var(--gold);
          color: var(--white);
          border: none;
          padding: 16px 36px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: background 0.25s;
          text-decoration: none;
        }
        .btn-gold:hover { background: var(--gold-dark); }
        .btn-ghost {
          font-family: var(--font-ui);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          background: transparent;
          color: var(--ink);
          border: 1px solid var(--ink);
          padding: 15px 32px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.22s;
          text-decoration: none;
        }
        .btn-ghost:hover { background: var(--ink); color: var(--white); }

        /* ── Stats ── */
        .hero-stats {
          display: flex;
          gap: 36px;
          margin-top: 48px;
          padding-top: 32px;
          border-top: 1px solid rgba(176,141,87,0.2);
          flex-wrap: wrap;
        }
        .hero-stat-num {
          font-family: var(--font-display);
          font-size: 26px;
          font-weight: 700;
          color: var(--ink);
        }
        .hero-stat-label {
          font-family: var(--font-ui);
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #9A9186;
          margin-top: 2px;
        }

        /* ── Mobile ── */
        @media (max-width: 768px) {
          .hero-section {
            margin-top: 105px;
            min-height: auto;
            flex-direction: column;
            align-items: flex-start;
          }
          .hero-bg {
            position: relative;
            width: 100%;
            height: 55vw;
            min-height: 240px;
            top: auto; right: auto;
          }
          .hero-bg::before {
            background: linear-gradient(to bottom, transparent 50%, var(--cream) 100%);
          }
          .hero-bg img {
            object-position: center top;
          }
          .hero-content {
            width: 100%;
            padding: 28px 24px 52px;
          }
          .hero-h1 {
            font-size: clamp(36px, 10vw, 60px);
          }
          .hero-stats { gap: 24px; margin-top: 32px; padding-top: 24px; }
        }
        @media (max-width: 480px) {
          .hero-ctas { flex-direction: column; align-items: flex-start; }
          .btn-gold, .btn-ghost { width: 100%; justify-content: center; }
        }
      `}</style>

      <section className="hero-section" ref={heroRef}>

        {/* Background image — crossfades between hero.png and hero1.png every 5s */}
        <div className="hero-bg">
          <motion.div style={{ y: imgY, position: 'absolute', inset: 0 }}>
            <AnimatePresence>
              {heroImages.map((src, i) =>
                i === imgIdx ? (
                  <motion.img
                    key={src}
                    src={src}
                    alt="Luxury jewellery"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.4, ease: 'easeInOut' }}
                    style={{
                      position: 'absolute',
                      top: 0, left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center center',
                    }}
                  />
                ) : null
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Text content */}
        <div className="hero-content">
          <motion.h1
            className="hero-h1"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Luxe. Light.<br />
            <WordSwap />
          </motion.h1>

          <motion.p
            className="hero-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            Jewelry that reflects your shine. Thoughtfully designed. Elegantly crafted. Infinitely loved.
          </motion.p>

          <motion.div
            className="hero-ctas"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
          >
            <Link to="/products" className="btn-gold">
              Shop Now <ArrowRight size={13} />
            </Link>
            <Link to="/about" className="btn-ghost">
              Our Story <ArrowUpRight size={13} />
            </Link>
          </motion.div>

          <motion.div
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.75 }}
          >
            {[
              { num: '500+',  label: 'Designs' },
              { num: '12K+',  label: 'Happy Customers' },
              { num: '100%',  label: 'Authentic' },
            ].map(s => (
              <div key={s.label}>
                <div className="hero-stat-num">{s.num}</div>
                <div className="hero-stat-label">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

      </section>
    </>
  );
};

export default HeroSection;